import React from 'react';
import { Link } from 'react-router-dom';
import { trackFreeTrial } from '../utils/tracking';

/**
 * /blog/best-sourcing-tools-pcrecruiter
 * SEO article targeting: pcrecruiter tools, pcrecruiter extensions,
 * pcr ats tools, best sourcing tools pcrecruiter
 */
const BlogPCRecruiterTools = () => {
  return (
    <>
      {/* Hero */}
      <section className="hero" style={{ paddingBottom: '40px' }}>
        <div className="container hero-content fade-up" style={{ maxWidth: '760px' }}>
          <div className="section-badge blue">Blog</div>
          <h1 className="hero-title" style={{ fontSize: '2.25rem', lineHeight: 1.2 }}>
            Best Sourcing Tools for <span className="text-gradient">PCRecruiter</span> Users
          </h1>
          <p className="hero-subtitle" style={{ fontSize: '1rem' }}>
            A practical guide to the tools that help recruiters using PCRecruiter source
            candidates, verify contacts, and fill roles faster.
          </p>
        </div>
      </section>

      {/* Article body */}
      <section style={{ paddingTop: 0 }}>
        <div className="container" style={{ maxWidth: '720px' }}>

          {/* Intro */}
          <div style={{ marginBottom: '48px' }}>
            <p style={{ fontSize: '1rem', color: 'var(--text)', lineHeight: 1.8, marginBottom: '16px' }}>
              PCRecruiter is one of the most established ATS platforms in the staffing industry, used by
              thousands of recruiting firms to manage candidates, contacts, and pipelines. But PCRecruiter
              — like any ATS — is only as good as the data inside it.
            </p>
            <p style={{ fontSize: '1rem', color: 'var(--text)', lineHeight: 1.8 }}>
              To fill roles faster, recruiters need tools that work <em>alongside</em> PCRecruiter
              to source net-new candidates, verify contact information, and identify hiring managers
              outside their existing database. Here are the best options in 2026.
            </p>
          </div>

          {/* Section 1: AI Sourcing Tools */}
          <div style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '16px' }}>
              1. AI-Powered Sourcing Tools
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.8, marginBottom: '20px' }}>
              AI sourcing tools go beyond keyword matching to understand the intent behind your search.
              Instead of building complex boolean strings, you describe what you need in plain English
              and the AI finds matching candidates.
            </p>

            <div className="card" style={{ padding: '28px', marginBottom: '16px', borderLeft: '4px solid var(--primary)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary)', marginBottom: '8px' }}>
                kNine.ai — Built for PCRecruiter
              </h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--text)', lineHeight: 1.7, marginBottom: '12px' }}>
                kNine is the only AI sourcing platform with a native PCRecruiter integration.
                It provides two AI agents: <strong>Roxi</strong> for candidate sourcing and <strong>Cooper</strong> for
                BD and company research. Sourced contacts push directly into PCRecruiter with verified
                emails, phone numbers, and enrichment data — no manual data entry.
              </p>
              <ul style={{ paddingLeft: '20px', margin: 0, listStyleType: 'disc' }}>
                <li style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>100M+ verified contacts with 95%+ email accuracy</li>
                <li style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>Natural language AI search — no boolean needed</li>
                <li style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>Native PCRecruiter integration (pre-configured, zero setup)</li>
                <li style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>Chrome extension for sourcing from any website</li>
                <li style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>Starting at $6,000/year with a free trial</li>
              </ul>
            </div>

            <div className="card-flat" style={{ padding: '24px', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                Other AI Sourcing Options
              </h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
                Tools like Seamless.AI and HireEZ offer AI-assisted search, but none have native
                PCRecruiter integrations. You'd need to manually export contacts or build a custom
                connection, adding friction to your workflow.
              </p>
            </div>
          </div>

          {/* Section 2: Chrome Extensions */}
          <div style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '16px' }}>
              2. Chrome Extensions for Recruiters
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.8, marginBottom: '20px' }}>
              Chrome extensions let you source candidates directly from LinkedIn, company websites,
              and job boards without leaving your browser. The best ones capture contact info and push
              it into your ATS automatically.
            </p>

            <div className="card-flat" style={{ padding: '24px', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                kNine Chrome Extension
              </h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
                Source candidates from LinkedIn profiles, company websites, and more. The kNine extension
                surfaces verified emails and phone numbers in-browser, and pushes them directly to
                PCRecruiter with one click.
              </p>
            </div>

            <div className="card-flat" style={{ padding: '24px', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                ContactOut / Lusha
              </h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
                Popular contact-finding Chrome extensions. Useful for grabbing emails quickly, but they
                lack PCRecruiter integration and require manual export/import to get data into your ATS.
              </p>
            </div>
          </div>

          {/* Section 3: Contact Verification */}
          <div style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '16px' }}>
              3. Contact Verification Tools
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.8, marginBottom: '20px' }}>
              Stale data is a major problem in recruiting. Candidates change jobs, emails bounce,
              and phone numbers go dead. Verification tools ensure your outreach actually reaches people.
            </p>

            <div className="card-flat" style={{ padding: '24px', marginBottom: '16px' }}>
              <p style={{ fontSize: '0.9rem', color: 'var(--text)', lineHeight: 1.7 }}>
                kNine includes <strong>real-time contact verification</strong> built into every lookup.
                When you source a candidate, the email and phone number are verified before the credit
                is consumed. This is included at no additional cost — unlike standalone verification
                services that charge per verification.
              </p>
            </div>
          </div>

          {/* Section 4: Recruiting Automation */}
          <div style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '16px' }}>
              4. Recruiting Automation Tools
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.8, marginBottom: '20px' }}>
              Automation helps recruiters scale outreach without scaling headcount. The best tools
              connect to your ATS so automated sequences use fresh, verified data.
            </p>

            <div className="card-flat" style={{ padding: '24px', marginBottom: '16px' }}>
              <p style={{ fontSize: '0.9rem', color: 'var(--text)', lineHeight: 1.7 }}>
                kNine's Enterprise plan includes <strong>auto enrichment</strong>, which automatically
                refreshes and updates contact records in your pipeline. Combined with PCRecruiter's
                workflow automation, this means your outreach lists stay current without manual effort.
              </p>
            </div>
          </div>

          {/* The Bottom Line */}
          <div style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '16px' }}>
              The Bottom Line
            </h2>
            <div style={{
              background: 'var(--primary-bg)',
              border: '1px solid #BBDEFB',
              borderRadius: 'var(--radius-lg)',
              padding: '24px 28px',
              marginBottom: '20px',
            }}>
              <p style={{ fontSize: '0.95rem', color: 'var(--primary-dark)', lineHeight: 1.7, fontWeight: 500, margin: 0 }}>
                PCRecruiter is excellent at managing your recruiting workflow. But to fill roles faster,
                you need tools that find candidates <em>outside</em> your existing database. The most
                effective approach is a sourcing platform that integrates natively with PCRecruiter —
                so new contacts flow directly into your workflow without friction.
              </p>
            </div>
          </div>

          {/* Article CTA */}
          <div className="card" style={{ padding: '36px', textAlign: 'center', marginBottom: '48px' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
              Try kNine Free with PCRecruiter
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '20px' }}>
              14-day free trial. No credit card. Native PCRecruiter integration included.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="https://app.knine.ai/register"
                className="btn btn-gradient btn-lg"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackFreeTrial('blog_pcr_cta')}
              >
                Start Free Trial
              </a>
              <Link to="/pcrecruiter-sourcing" className="btn btn-outline btn-lg">
                Learn More
              </Link>
            </div>
          </div>

          {/* Related links */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '24px' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px' }}>
              Related Pages
            </h4>
            <ul style={{ paddingLeft: '20px', margin: 0, listStyleType: 'disc' }}>
              <li style={{ marginBottom: '8px' }}>
                <Link to="/pcrecruiter-sourcing" style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>
                  AI Candidate Sourcing for PCRecruiter
                </Link>
              </li>
              <li style={{ marginBottom: '8px' }}>
                <Link to="/integrations" style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>
                  All kNine Integrations
                </Link>
              </li>
              <li style={{ marginBottom: '8px' }}>
                <Link to="/comparisons" style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>
                  Compare kNine to ZoomInfo, Apollo, Lusha & More
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPCRecruiterTools;
